import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, Video, Trash2, Play } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { compressImage } from "@/lib/compressImage";

export const MediaManager = ({ businessId }) => {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(null);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const load = () => api.get(`/media/${businessId}`).then(({ data }) => setItems(data)).catch((e) => console.error(e));
  useEffect(() => { if (businessId) load(); /* eslint-disable-next-line */ }, [businessId]);

  const photos = items.filter((m) => m.media_type === "photo");
  const videos = items.filter((m) => m.media_type === "video");

  const uploadOne = async (file, type) => {
    const fd = new FormData();
    fd.append("file", type === "photo" ? await compressImage(file) : file);
    fd.append("media_type", type);
    const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setItems((x) => [data, ...x]);
  };

  const upload = async (files, type) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const free = type === "video" ? 2 - videos.length : 10 - photos.length;
    const batch = list.slice(0, Math.max(0, free));
    const skipped = list.length - batch.length;
    setUploading(type);
    let done = 0;
    for (const file of batch) {
      try { await uploadOne(file, type); done += 1; }
      catch (e) { toast.error(`${file.name}: ${formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"}`); }
    }
    setUploading(null);
    if (done) toast.success(`${done} ${type === "video" ? "video" : "photo"}${done > 1 ? "s" : ""} uploaded`);
    if (skipped > 0) toast.info(`${skipped} file${skipped > 1 ? "s" : ""} skipped — gallery limit reached (${type === "video" ? 2 : 10} max)`);
  };

  const remove = async (id) => {
    try { await api.delete(`/media/${id}`); setItems((x) => x.filter((m) => m.id !== id)); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="media-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="font-display font-bold text-slate-900">Business Gallery</p>
          <p className="text-sm text-slate-500">Up to {10} photos and {2} videos. Photos are auto-optimised; videos ≤50MB.</p>
        </div>
        <div className="flex gap-2">
          <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="media-photo-input" onChange={(e) => { upload(e.target.files, "photo"); e.target.value = ""; }} />
          <input ref={videoRef} type="file" accept="video/*" className="hidden" data-testid="media-video-input" onChange={(e) => { upload(e.target.files, "video"); e.target.value = ""; }} />
          <Button type="button" data-testid="add-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploading || photos.length >= 10} variant="outline" className="gap-1.5 border-slate-300 font-semibold">
            {uploading === "photo" ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />} Photo ({photos.length}/10)
          </Button>
          <Button type="button" data-testid="add-video-btn" onClick={() => videoRef.current?.click()} disabled={uploading || videos.length >= 2} variant="outline" className="gap-1.5 border-slate-300 font-semibold">
            {uploading === "video" ? <Loader2 className="animate-spin" size={16} /> : <Video size={16} />} Video ({videos.length}/2)
          </Button>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">No media yet — add photos and videos to showcase your business.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="media-manager-grid">
          {items.map((m) => (
            <div key={m.id} data-testid={`media-tile-${m.id}`} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group">
              {m.media_type === "video" ? (
                <><video src={mediaUrl(m.url)} className="h-full w-full object-cover" /><span className="absolute inset-0 flex items-center justify-center bg-black/25"><Play size={22} className="text-white" /></span></>
              ) : (
                <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" />
              )}
              <button data-testid={`delete-media-${m.id}`} onClick={() => remove(m.id)} className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
